import React, { useState } from "react";
import { getBrandName } from "../getBrandName";

export const useCookiePolicy = () => {
  const [active, setActive] = useState("what");
  const { brandName , normalizedBrand,phone } = getBrandName();

  const sections = [
    { id: "what", title: "What Are Cookies?" },
    { id: "why", title: `Why We Use Cookies at ${brandName}` },
    { id: "types", title: "Types of Cookies We Use" },
    { id: "thirdparty", title: "Third-Party Cookies" },
    { id: "manage", title: "Managing Your Cookie Preferences" },
    { id: "duration", title: "How Long Are Cookies Stored?" },
    { id: "updates", title: "Updates to This Policy" },
    { id: "questions", title: "Questions or Concerns?" },
  ];

  const handleScrollTo = (id) => {
    setActive(id);
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const generateContent = (id) => {
    if (id === "why") {
      return (
        <>
          <p>
            {brandName} uses cookies to improve functionality, security, and performance. The key purposes include:
          </p>

          <div className="cookie-purpose">
            <h5><i className="bi bi-shield-lock-fill text-success"></i> Authentication & Session Management</h5>
            <ul>
              <li>Keep you signed in during your visit</li>
              <li>Authenticate users securely</li>
              <li>Prevent session hijacking</li>
            </ul>
          </div>

          <div className="cookie-purpose">
            <h5><i className="bi bi-sliders text-info"></i> Preference Management</h5>
            <ul>
              <li>Remember your display language</li>
              <li>Save region or timezone preferences</li>
              <li>Maintain interface settings</li>
            </ul>
          </div>

          <div className="cookie-purpose">
            <h5><i className="bi bi-bar-chart-line text-primary"></i> Performance Tracking</h5>
            <ul>
              <li>Understand how users navigate the platform</li>
              <li>Measure usage (e.g., time on page, clicks)</li>
              <li>Identify bugs or areas of improvement</li>
            </ul>
          </div>

          <div className="cookie-purpose">
            <h5><i className="bi bi-person-check text-warning"></i> Personalization & Recommendations</h5>
            <ul>
              <li>Tailor analytics and reporting dashboards to your preferences</li>
              <li>Serve relevant content or reminders based on behavior</li>
            </ul>
          </div>

          <div className="cookie-purpose">
            <h5><i className="bi bi-shield-exclamation text-danger"></i> Security & Fraud Detection</h5>
            <ul>
              <li>Detect unusual behavior patterns</li>
              <li>Prevent cross-site scripting or brute force attacks</li>
            </ul>
          </div>
        </>
      );
    }

    if (id === "types") {
      return (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              fontWeight: "bold",
              paddingBottom: "0.5rem",
              borderBottom: "1px solid #ccc",
              marginBottom: "1rem",
            }}
          >
            <div>Cookie Type</div>
            <div>Purpose</div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              rowGap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <div><strong>Necessary Cookies</strong></div>
            <div>Critical for site function: login, page navigation, authentication, security</div>

            <div><strong>Performance Cookies</strong></div>
            <div>Monitor visitor behavior for analytics and UX improvements</div>

            <div><strong>Functional Cookies</strong></div>
            <div>Remember preferences like language, layout, and saved filters</div>

            <div><strong>Marketing Cookies</strong></div>
            <div>Show personalized content or ads <strong>(only with your consent)</strong></div>
          </div>

          <p className="text-success">
            <i className="bi bi-check-circle-fill"></i> You can control Functional and Marketing Cookies directly from the cookie consent banner.
          </p>
        </>
      );
    }

    const contentMap = {
      what: `
Cookies are small data files that websites store on your device (computer, tablet, or smartphone) through your browser. They help websites remember your actions or preferences over time and can improve your browsing experience by:

• Keeping you signed in  
• Remembering your settings  
• Understanding your behavior to enhance usability  

LOCK_ICON Cookies do not store sensitive personal information like passwords or payment data, and cannot install malware or viruses on your system.
`,

      thirdparty: `
We may use trusted third-party service providers to improve our services. These providers may place their own cookies, which are governed by their respective privacy and cookie policies.

Examples include:  
• Google Analytics – For aggregated website usage data  
• Payment Providers (e.g., Razorpay, Stripe) – For secure transaction handling  
• Social Media Plugins (e.g., LinkedIn, Twitter, YouTube, WhatsApp, Facebook) – To enable easy content sharing  
• Customer Support Tools (e.g., Intercom, Freshchat, Purechat) – For real-time interaction  

LINK_ICON You are encouraged to review the cookie and privacy policies of these providers directly for more detailed information.
`,

      manage: `
You have complete control over how cookies are used. You can:

a. Via Our Cookie Banner  
• Accept all cookies  
• Customize which types of cookies you consent to  
• Revoke or change your consent anytime

b. Via Browser Settings  
Most modern browsers (like Chrome, Firefox, Safari) allow you to:  
• View cookies stored  
• Delete cookies individually or all at once  
• Block new cookies altogether  
• Set site-specific cookie rules

c. Private/Incognito Browsing  
Using private or incognito mode will prevent long-term storage of cookies after the session ends.

WARN_ICON Disabling essential cookies may affect core platform functionality such as secure login, personalized dashboards, and API access.
`,

      duration: `
• Session Cookies: Deleted automatically when you close your browser  
• Persistent Cookies: Remain on your device until expiry or manual deletion (usually 30–365 days)

You can view exact durations in your browser’s storage settings.
`,

      updates: `
We may update this Cookie Policy periodically due to:  
• Legal or regulatory changes  
• Improvements to platform functionality  
• Integration with new third-party tools

Any changes will be published on this page with a revised "Effective Date" and a clear notification via the cookie banner or platform message.
`,

      questions: `
For concerns about cookie use or to exercise your rights under the Digital Personal Data Protection Act, 2023, contact:

• Email: ${normalizedBrand}@gmail.com  
• Contact: ${phone } 
• Website: www.${normalizedBrand}.com

We’re committed to giving you control, clarity, and confidence when it comes to your data.
`,
    };

    const lines = contentMap[id]?.split("\n") || [];
    let items = [];
    const contentElements = [];

    const flushList = () => {
      if (items.length) {
        contentElements.push(
          <ul key={Math.random()}>
            {items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        );
        items = [];
      }
    };

    lines.forEach((line, index) => {
      if (!line.trim()) return;

      if (line.includes("|")) {
        flushList();
        const [left, right] = line.split("|").map((s) => s.trim());
        contentElements.push(
          <p key={`row-${index}`}>
            <strong>{left}</strong>: {right}
          </p>
        );
      } else if (/^•|SUCCESS_ICON|GEAR_ICON|GRAPH_ICON|TARGET_ICON|SHIELD_ICON|LINK_ICON|WARN_ICON|LOCK_ICON/.test(line)) {
        let icon = null;
        if (line.includes("SUCCESS_ICON")) icon = <i className="bi bi-check-circle-fill text-success me-2"></i>;
        else if (line.includes("GEAR_ICON")) icon = <i className="bi bi-gear-fill me-2"></i>;
        else if (line.includes("GRAPH_ICON")) icon = <i className="bi bi-graph-up-arrow me-2"></i>;
        else if (line.includes("TARGET_ICON")) icon = <i className="bi bi-target me-2"></i>;
        else if (line.includes("SHIELD_ICON")) icon = <i className="bi bi-shield-fill-check text-primary me-2"></i>;
        else if (line.includes("LINK_ICON")) icon = <i className="bi bi-link-45deg me-2"></i>;
        else if (line.includes("WARN_ICON")) icon = <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>;
        else if (line.includes("LOCK_ICON")) icon = <i className="bi bi-lock-fill me-2"></i>;
        
        const text = line.replace(/^(\s*•|\s*[\u{1F300}-\u{1F6FF}])\s*/u, "").trim();
        items.push(<>{icon}{text}</>);
      } else if (/^[a-z]\./i.test(line)) {
        flushList();
        contentElements.push(
          <h6 key={`subheading-${index}`} style={{ marginTop: "1rem" }}>
            {line}
          </h6>
        );
      } else {
        flushList();
        contentElements.push(
          <p key={`para-${index}`} style={{ marginBottom: "0.5rem" }}>
            {line}
          </p>
        );
      }
    });

    flushList();
    return contentElements;
  };

  return { sections, active, handleScrollTo, generateContent };
};
